"use client"

import { UiIcon } from '@/components/ui/ui-icon'
import Link from "next/link"
import { useCallback, useState, useEffect, useMemo, useRef } from "react"
import { createPortal } from "react-dom"
import { usePathname } from "next/navigation"
import {
  Calculator,
  ChevronDown,
  Compass,
  Menu,
  ScanSearch,
  Search,
  ShoppingBag,
  Smartphone,
  Stethoscope,
  X,
} from "lucide-react"
import { Button } from "./button"
import Image from "next/image"
import { BRAND_ASSET_PATHS } from "@/lib/brand-assets"
import { ToolIllustration } from "@/components/tools/tool-illustration"
import { NavSectionIllustration, hasNavSectionIllustration } from "@/components/ui/nav-section-illustration"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./dropdown-menu"
import { LoginButton } from "@/components/auth/LoginButton"
import { useAuth } from "@/contexts/AuthContext"
import { communityAnalytics, trackEvent } from "@/lib/analytics"
import { getActiveTools } from "@/data/tools-manifest"
import { identifyCtaProps } from "@/lib/identify-cta"
import { useModalFocusManagement } from "@/hooks/use-modal-focus-management"
import { useAppInstallAvailable } from "@/hooks/use-app-install-available"

const HOME_UPLOAD_HREF = "/#analysis-card"

// Persistent app entry in the header pill. Points at the /app landing page, not
// straight at the App Store: the page carries the screenshots, the pricing, the
// QR handoff for desktop, and its own tracked badge, so one destination serves
// every device. That also keeps this out of the `app_download_*` cta_location
// namespace, which means "this click left for apps.apple.com" — pooling a page
// visit in there would inflate every App Store placement's CTR.
//
// No `cta_surface_shown` here, matching the other nav CTAs: these render on
// every page load, so the impression count would just restate $pageview. See
// the IMPRESSION COVERAGE note in lib/identify-cta.ts.
const APP_ENTRY_HREF = "/app"
const APP_ENTRY_LABEL = "iPhone app"
const APP_ENTRY_LOCATION = {
  header: "header_app_entry",
  mobileHeader: "mobile_header_app_entry",
} as const

function appEntryCtaProps(location: string) {
  return {
    cta_location: location,
    cta_text: APP_ENTRY_LABEL,
    destination: "app_landing",
    destination_url: APP_ENTRY_HREF,
  }
}
const ACTIVE_TOOLS = getActiveTools()
const ACTIVE_TOOL_COUNT = ACTIVE_TOOLS.length
const POPULAR_TOOL_IDS = [
  "lawn-size-calculator",
  "soil-temperature",
  "fertilizer-calculator",
  "watering-schedule",
  "mowing-calculator",
  "herbicide-timing",
]
const POPULAR_TOOLS = POPULAR_TOOL_IDS.flatMap((id) => {
  const tool = ACTIVE_TOOLS.find((item) => item.id === id)
  return tool ? [tool] : []
})

const desktopNavLinks = [
  { href: "/diagnose", label: "Diagnose", section: "diagnose" },
  { href: "/guides", label: "Guides", section: "guides" },
  { href: "/products", label: "Products", section: "products" },
]

// Labels are short because the mobile Browse list renders these two-up (see
// renderMobileNavLink) — a half-width tile fits roughly 13 characters before
// it truncates. The desktop Explore dropdown shows the same strings; it has
// room to spare either way, so one label serves both rather than carrying a
// parallel `shortLabel` field that only one surface reads.
export const HEADER_EXPLORE_LINKS = [
  { href: "/grass-atlas?source=header", label: "Grass in 3D", section: "grass-atlas" },
  { href: "/community", label: "Community", section: "community" },
  { href: "/directory", label: "Directory", section: "directory" },
  { href: "/weeds", label: "Weeds", section: "weeds" },
  { href: "/pests", label: "Pests", section: "pests" },
  { href: "/blog", label: "Blog", section: "blog" },
  { href: "/games", label: "Arcade", section: "games" },
  { href: "/app", label: "iPhone app", section: "app" },
  { href: "/about", label: "About", section: "about" },
]

const mobileBrowseLinks = [
  { href: "/guides", label: "Guides", section: "guides" },
  ...HEADER_EXPLORE_LINKS,
]

// Effective sticky-header height = top gap (pt-3 = 12px) + the frosted pill's
// full box: 1px ds-glass top border + row height + 1px bottom border. Single
// source of truth for the three consumers that used to hardcode 64/80:
// scrollToUpload's offset, the mobile drawer's top/max-h, and the portaled
// backdrop's top. Update these together if the pill height changes.
// (Desktop value browser-measured at pillBottom = 78px, confirming the +2 border.)
const HEADER_OFFSET_MOBILE = 70 // 12 (pt-3) + 1 + 56 (h-14 row) + 1 border
const HEADER_OFFSET_DESKTOP = 78 // 12 (pt-3) + 1 + 64 (h-16 row) + 1 border

export function Header() {
  const pathname = usePathname()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const mobileMenuRef = useRef<HTMLDivElement>(null)
  // The floating pill hosts the drawer's open/close toggle. Keep it interactive
  // while the drawer is open (it lives outside the drawer, so the focus manager
  // would otherwise inert it and leave the top X a dead control).
  const pillRef = useRef<HTMLDivElement>(null)
  const keepPillInteractive = useMemo(() => [pillRef], [])
  // The app is iPhone-only, so an /app visit is a dead end on Android. Same
  // gate every other app surface uses (hooks/use-app-install-available.ts):
  // detect Android and hide, rather than detect iOS and show.
  const appInstallAvailable = useAppInstallAvailable()
  // Drives WHERE the drawer's auth control renders, not what it shows.
  // Signed out, LoginButton's mobile variant is one compact button, so it goes
  // above the task list where a Sign In prompt is actually visible. Signed in,
  // it expands to an avatar row plus Dashboard/Billing/Settings/Sign out — a
  // stack that tall at the top would push the tasks off screen, so it stays at
  // the bottom. Gated on `initialized` so we never place it on a guess; the
  // drawer only mounts on tap, long after auth settles, so there is no visible
  // reflow in practice.
  const { user: authUser, initialized: authInitialized } = useAuth()
  const authAtTop = authInitialized && !authUser
  // Prevent hydration mismatch by waiting for client mount
  useEffect(() => {
    setMounted(true)
  }, [])
  const [isToolsExpanded, setIsToolsExpanded] = useState(false)

  const toggleMenu = () => {
    setIsMenuOpen((open) => !open)
  }

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false)
    setIsToolsExpanded(false)
  }, [])

  useModalFocusManagement({
    isOpen: isMenuOpen,
    containerRef: mobileMenuRef,
    keepInteractive: keepPillInteractive,
    onEscape: closeMenu,
  })

  const scrollToUpload = () => {
    requestAnimationFrame(() => {
      const el = document.getElementById("analysis-card")
      if (!el) return

      // Floating pill: effective sticky height = top gap + pill row height.
      const headerOffset =
        typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches
          ? HEADER_OFFSET_DESKTOP
          : HEADER_OFFSET_MOBILE
      const padding = 16
      const viewportH = window.innerHeight
      const rect = el.getBoundingClientRect()
      const cardH = rect.height
      const availableH = viewportH - headerOffset

      let targetY: number
      if (cardH + padding * 2 <= availableH) {
        // Card fits in the visible area below the header — center it there
        // so the user sees the whole upload UI without having to scroll.
        const centerOffset = (availableH - cardH) / 2
        targetY = window.scrollY + rect.top - headerOffset - centerOffset
      } else {
        // Card is taller than the visible area — pin its top under the header.
        targetY = window.scrollY + rect.top - headerOffset - padding
      }

      window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" })

      // Briefly highlight the upload button after the scroll completes so
      // it's clear where the click is sending the user.
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent("highlightUploadCta"))
      }, 450)
    })
  }

  const handleIdentifyClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    ctaLocation: string,
  ) => {
    closeMenu()
    trackEvent("cta_clicked", identifyCtaProps(ctaLocation))

    if (pathname === "/") {
      e.preventDefault()
      if (document.getElementById("analysis-card")) {
        scrollToUpload()
        return
      }
      window.dispatchEvent(new CustomEvent("homeReset"))
      scrollToUpload()
    }
  }

  // Clicking Home or the logo from inside `/` would otherwise be a no-op —
  // Next.js skips remounting the route, so any in-page state (e.g. the homepage
  // grass-ID results card) stays put. Normally we force a hard navigation so
  // the user gets the fresh homepage they expect, but during an in-flight
  // analysis (`document.body.dataset.analyzing === 'true'`) a hard reload
  // would kill the request — instead dispatch a `homeReset` CustomEvent that
  // the homepage hook subscribes to and treat that as a soft reset.
  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    closeMenu()
    if (pathname === '/') {
      e.preventDefault()
      const isAnalyzing = typeof document !== 'undefined'
        && document.body.dataset.analyzing === 'true'
      const hasHomeAnalysisState = typeof document !== 'undefined'
        && document.body.dataset.homeAnalysisActive === 'true'
      if (isAnalyzing || hasHomeAnalysisState) {
        window.dispatchEvent(new CustomEvent('homeReset'))
      } else {
        window.location.assign('/')
      }
    }
  }

  const toggleTools = () => {
    setIsToolsExpanded((expanded) => !expanded)
  }

  // Lock scroll while the mobile drawer is open so the page behind it doesn't
  // scroll under the menu on touch devices. Lock <html>, NOT <body>:
  // `overflow: hidden` on <body> turns it into a scroll container, which
  // unsticks the `position: sticky` header (it jumps to top: -scrollY) and
  // drags the absolutely-positioned drawer off-screen with it — the drawer then
  // looks "cut off" when opened after scrolling. Locking <html> preserves the
  // sticky header while still preventing background scroll.
  useEffect(() => {
    if (!isMenuOpen) return
    const original = document.documentElement.style.overflow
    document.documentElement.style.overflow = 'hidden'
    return () => {
      document.documentElement.style.overflow = original
    }
  }, [isMenuOpen])

  const renderDesktopNavLink = (link: (typeof desktopNavLinks)[number]) => {
    const linkPath = link.href.split('?')[0] ?? link.href
    const isActive = linkPath === '/'
      ? pathname === '/'
      : pathname === linkPath || pathname.startsWith(linkPath + '/')

    return (
      <Link
        key={link.href}
        href={link.href}
        prefetch={false}
        onClick={link.href === '/' ? handleHomeClick : undefined}
        aria-current={isActive ? 'page' : undefined}
        className={`group relative inline-flex min-h-11 items-center whitespace-nowrap rounded-full px-2.5 py-2 font-medium transition-colors duration-300 xl:px-3 ${
          isActive
            ? 'text-green-700 bg-white/65'
            : 'text-gray-700 hover:text-green-700 hover:bg-white/60'
        }`}
      >
        <span className="relative z-10">{link.label}</span>
      </Link>
    )
  }

  const renderMobileNavLink = (link: (typeof HEADER_EXPLORE_LINKS)[number]) => {
    const linkPath = link.href.split('?')[0] ?? link.href
    const isActive = linkPath === '/'
      ? pathname === '/'
      : pathname === linkPath || pathname.startsWith(linkPath + '/')

    return (
      <Link
        key={link.href}
        href={link.href}
        prefetch={false}
        onClick={() => {
          if (link.href === '/community') {
            communityAnalytics.galleryCtaClicked({ source: 'mobile_browse' })
          }
          closeMenu()
        }}
        aria-current={isActive ? 'page' : undefined}
        className={`px-2.5 py-2 text-[13.5px] font-medium rounded-xl transition-colors duration-300 min-h-11 flex items-center gap-2 touch-manipulation group ${
          isActive
            ? 'text-green-800 bg-white/60'
            : 'text-gray-800 hover:text-green-700 hover:bg-white/55'
        }`}
      >
        <span className="grid h-7 w-7 flex-none place-items-center rounded-lg border border-white/70 bg-white/60 text-green-700 transition-transform duration-200 group-hover:scale-105 [&>svg]:h-4 [&>svg]:w-4">
          {hasNavSectionIllustration(link.section) ? (
            <NavSectionIllustration section={link.section} />
          ) : (
            <UiIcon icon={Compass} aria-hidden="true" />
          )}
        </span>
        <span className="min-w-0 truncate">{link.label}</span>
      </Link>
    )
  }

  return (
    <header className="sticky top-0 z-50 pointer-events-none pt-3">
      <div ref={pillRef} className="content-rail">
        {/* Floating frosted pill (3a). ds-glass carries the blur/saturate/sheen +
            reduced-transparency/unsupported-backdrop fallbacks. pointer-events are
            re-enabled here because the <header> wrapper turns them off so the top
            gap doesn't eat clicks on page content peeking above the pill. */}
        <div className="ds-glass pointer-events-auto rounded-full px-3 lg:px-4">
          {/* Mobile pill row: [Logo] — [iPhone app] [Menu] */}
          <div className="relative flex items-center justify-between gap-2 min-h-14 lg:hidden">
            {/* Left: Logo + Brand Name */}
            <Link
              href="/"
              className="flex min-h-11 min-w-0 items-center gap-1.5 group"
              onClick={handleHomeClick}
            >
              <Image
                src={BRAND_ASSET_PATHS.iconDefault}
                alt="What Grass Is This? Logo"
                width={32}
                height={32}
                className="h-7 w-7 flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
              />
              <span className="text-xs min-[400px]:text-sm font-semibold tracking-tight text-green-900 group-hover:text-green-700 transition-colors leading-tight">
                What Grass Is This?
              </span>
            </Link>

            {/* Right: app entry + hamburger. Signed-in avatar / Sign in live in
                the drawer (LoginButton variant="mobile") — 3a's mobile bar
                carries two actions, not an auth chip.

                ONE slot, two occupants. iOS and desktop get the app entry;
                Android falls back to the Identify shortcut, because /app sells
                an iPhone-only app and the slot must never sit empty. Both are
                44x44, so the Android correction on hydration costs no layout
                shift — only the corner radius differs.

                Identify moved out of this bar deliberately (2026-08-09) and now
                lives only as the drawer's first row. To put it back beside the
                app entry, render BOTH children here instead of branching. */}
            <div className="flex flex-shrink-0 items-center gap-2">
              {appInstallAvailable ? (
                <Link
                  href={APP_ENTRY_HREF}
                  prefetch={false}
                  aria-label="Get the iPhone app"
                  onClick={() =>
                    trackEvent("cta_clicked", appEntryCtaProps(APP_ENTRY_LOCATION.mobileHeader))
                  }
                  className="grid h-11 w-11 place-items-center rounded-xl border border-green-400/55 bg-green-900/90 text-green-300 shadow-[0_0_0_3px_rgba(74,222,128,0.14),0_6px_16px_-8px_rgba(20,83,45,0.6)] transition-transform active:scale-95"
                >
                  <UiIcon icon={Smartphone} size={20} />
                </Link>
              ) : (
                <Link
                  href={HOME_UPLOAD_HREF}
                  prefetch={false}
                  aria-label="Identify my grass"
                  onClick={(e) => handleIdentifyClick(e, "mobile_header_identify_grass")}
                  className="grid h-11 w-11 place-items-center rounded-full bg-linear-to-r from-green-600 to-emerald-600 text-white shadow-[0_6px_16px_-4px_rgba(22,163,74,0.4)] transition-transform active:scale-95"
                >
                  <UiIcon icon={ScanSearch} size={20} />
                </Link>
              )}
              {/* Single close/open control. The pill is kept interactive while
                  the drawer is open (keepPillInteractive), so this X actually
                  closes the menu — it's the drawer's only visible close, right
                  where the thumb opened it. */}
              <button
                onClick={toggleMenu}
                className="grid h-11 w-11 place-items-center rounded-full border border-white/70 bg-white/55 text-green-900 transition-colors hover:bg-white/80 focus:outline-hidden focus:ring-2 focus:ring-green-200"
                aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMenuOpen}
                aria-controls="mobile-site-navigation"
              >
                {isMenuOpen ? (
                  <UiIcon icon={X} className="w-6 h-6" />
                ) : (
                  <UiIcon icon={Menu} className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Desktop pill row */}
          <div className="hidden lg:flex justify-between items-center h-16">
          {/* Brand Logo */}
          <Link href="/" className="flex min-h-11 min-w-11 items-center group" onClick={handleHomeClick}>
            <div className="flex items-center space-x-3">
              <div className="flex items-center">
                {/* No `priority` here: the 40px nav logo is not the LCP
                    candidate, so preloading it claims a slot the actual
                    above-the-fold paint elements need. Browsers fetch it
                    eagerly via normal layout discovery. */}
                <Image
                  src={BRAND_ASSET_PATHS.iconDefault}
                  alt="What Grass Is This? Logo"
                  width={40}
                  height={40}
                  className="h-10 w-10 transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <span className="hidden text-xl font-semibold tracking-tight text-green-900 transition-colors duration-300 leading-none whitespace-nowrap group-hover:text-green-700 xl:inline">
                What Grass Is This?
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-0.5 lg:flex xl:gap-1">
            {desktopNavLinks.slice(0, 2).map(renderDesktopNavLink)}

            {mounted ? (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger className="group relative inline-flex min-h-11 items-center gap-1 rounded-full px-2.5 py-2 font-medium text-gray-800 outline-hidden transition-colors duration-300 hover:bg-white/60 hover:text-green-700 xl:px-3">
                  <span>Tools</span>
                  <UiIcon icon={ChevronDown} className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="center"
                  sideOffset={8}
                  collisionPadding={16}
                  className="w-[38rem] max-w-[calc(100vw-2rem)] overflow-visible rounded-2xl border-0 bg-transparent p-0 shadow-none"
                >
                  <div className="ds-glass overflow-hidden rounded-2xl">
                    <div className="bg-white/45 p-3">
                      <div className="mb-2 px-2 text-[11px] font-bold uppercase tracking-[0.08em] text-green-700">
                        Popular calculators
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {POPULAR_TOOLS.map((tool) => (
                          <DropdownMenuItem key={tool.href} asChild className="rounded-xl p-0 focus:bg-white/60 data-highlighted:bg-white/60">
                            <Link
                              href={tool.href}
                              prefetch={false}
                              className="group flex w-full cursor-pointer items-start gap-3 px-3 py-3 outline-hidden focus-visible:ring-0 focus-visible:ring-offset-0"
                            >
                              <span className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-green-100 bg-green-50/80 text-green-700">
                                <ToolIllustration href={tool.href} />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm font-semibold leading-snug text-gray-900">{tool.name}</span>
                                <span className="mt-0.5 block line-clamp-2 text-xs leading-relaxed text-gray-600">{tool.description}</span>
                              </span>
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </div>
                      <DropdownMenuSeparator className="my-2" />
                      <DropdownMenuItem asChild className="rounded-xl p-0 focus:bg-white/60 data-highlighted:bg-white/60">
                        <Link
                          href="/tools#tool-directory"
                          prefetch={false}
                          className="flex w-full cursor-pointer items-center justify-between gap-3 px-3 py-2.5 text-sm font-semibold text-green-700 outline-hidden"
                        >
                          <span className="inline-flex items-center gap-2"><Search className="h-4 w-4" />Search all {ACTIVE_TOOL_COUNT} tools</span>
                          <span aria-hidden="true">→</span>
                        </Link>
                      </DropdownMenuItem>
                    </div>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="inline-flex items-center gap-1 rounded-full px-2.5 py-2 font-medium text-gray-800 xl:px-3">
                Tools <UiIcon icon={ChevronDown} className="h-4 w-4" />
              </div>
            )}

            {desktopNavLinks.slice(2).map(renderDesktopNavLink)}

            {mounted ? (
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger className="group inline-flex min-h-11 items-center gap-1 rounded-full px-2.5 py-2 font-medium text-gray-800 outline-hidden transition-colors duration-300 hover:bg-white/60 hover:text-green-700 xl:px-3">
                  Explore
                  <UiIcon icon={ChevronDown} className="h-4 w-4 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" sideOffset={8} className="ds-glass w-64 rounded-2xl border-white/55 bg-white/75 p-2">
                  {HEADER_EXPLORE_LINKS.map((link) => (
                    <DropdownMenuItem key={link.href} asChild className="rounded-xl p-0">
                      <Link
                        href={link.href}
                        prefetch={false}
                        onClick={link.href === '/community'
                          ? () => communityAnalytics.galleryCtaClicked({ source: 'header_explore' })
                          : undefined}
                        className="flex min-h-11 items-center gap-3 px-3 py-2 text-sm font-medium text-gray-800"
                      >
                        <span className="grid h-8 w-8 flex-none place-items-center text-green-700 [&>svg]:h-5 [&>svg]:w-5">
                          {hasNavSectionIllustration(link.section) ? (
                            <NavSectionIllustration section={link.section} />
                          ) : (
                            <UiIcon icon={Compass} aria-hidden="true" />
                          )}
                        </span>
                        {link.label}
                      </Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="inline-flex items-center gap-1 rounded-full px-2.5 py-2 font-medium text-gray-800 xl:px-3">
                Explore <UiIcon icon={ChevronDown} className="h-4 w-4" />
              </div>
            )}

            {/* App entry sits LEFT of Identify so the primary scan CTA still
                closes the row. Dark fill + bright hairline reads as a separate
                destination rather than a fourth nav link, and it cannot be
                mistaken for the green Identify gradient beside it. */}
            {appInstallAvailable && (
              <Link
                href={APP_ENTRY_HREF}
                prefetch={false}
                onClick={() =>
                  trackEvent("cta_clicked", appEntryCtaProps(APP_ENTRY_LOCATION.header))
                }
                className="ms-2 inline-flex min-h-11 items-center gap-2 whitespace-nowrap rounded-full border border-green-400/55 bg-green-900/90 px-3.5 text-sm font-semibold text-green-50 shadow-[0_0_0_3px_rgba(74,222,128,0.14),0_6px_16px_-8px_rgba(20,83,45,0.6)] transition-colors hover:bg-green-950"
              >
                <UiIcon icon={Smartphone} size={20} className="text-green-300" aria-hidden="true" />
                {APP_ENTRY_LABEL}
              </Link>
            )}

            <Button
              asChild
              size="sm"
              className="ms-2 inline-flex rounded-full bg-linear-to-r from-green-700 to-emerald-700 px-4 text-white shadow-[0_8px_20px_-6px_rgba(22,163,74,0.4)] hover:from-green-800 hover:to-emerald-800"
            >
              <Link
                href={HOME_UPLOAD_HREF}
                prefetch={false}
                onClick={(e) => handleIdentifyClick(e, "header_identify_grass")}
              >
                <UiIcon icon={ScanSearch} className="h-4 w-4" />
                <span className="hidden xl:inline">Identify my grass</span>
                <span className="xl:hidden">Identify</span>
              </Link>
            </Button>

            {/* LoginButton sizes itself per auth state; its skeleton matches the final
                shape for the common (correct-hint) case, so no width reservation needed.
                tone="glass" swaps the signed-out Sign In pill to translucent white. */}
            <div className="ml-2 lg:ml-4 flex items-center">
              <LoginButton tone="glass" />
            </div>
          </nav>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu — rendered OUTSIDE the pill (and the max-width
          container) so the drawer + backdrop are viewport-width and escape the
          pill's `backdrop-filter` containing block (which would otherwise trap
          `position: fixed` descendants). Positioned from the shared header offset. */}
      {isMenuOpen && (
        <>
          {/* Dimming backdrop — portaled to <body>, starts just below the pill. */}
          {mounted && createPortal(
            <div
              className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-black/40 pointer-events-auto animate-in fade-in duration-200 ease-out motion-reduce:animate-none"
              style={{ top: HEADER_OFFSET_MOBILE }}
              onClick={closeMenu}
              aria-hidden="true"
            />,
            document.body,
          )}
          <div
            ref={mobileMenuRef}
            className="lg:hidden fixed left-0 right-0 z-60 px-3 pointer-events-auto animate-in fade-in slide-in-from-top-2 duration-200 ease-out motion-reduce:animate-none"
            style={{ top: HEADER_OFFSET_MOBILE }}
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
          >
            <div className="ds-glass overflow-hidden rounded-3xl shadow-xl">
            <nav
              id="mobile-site-navigation"
              // pb-8, not py-4: after the two-column compaction the content
              // fits maxHeight with ~4px to spare, so scrollHeight equalled
              // clientHeight and the list stopped being scrollable at all.
              // That is fine until anything covers the bottom of the viewport
              // (the Mediavine anchor, a browser bar) — with no scroll range
              // the covered rows become unreachable rather than merely
              // off-screen. The extra bottom padding keeps a real scroll range
              // and stops the last row kissing the panel edge.
              className="px-3 pt-4 pb-8 space-y-1 overflow-y-auto scrollbar-hide bg-white/45"
              style={{
                // `dvh`, not `vh`: on mobile Safari `100vh` is the URL-bar-COLLAPSED
                // height, so the drawer would size itself taller than the visible
                // area, scrollHeight would never exceed clientHeight, and the list
                // simply could not be scrolled to its end (it rubber-banded instead).
                // The dock var carries the Mediavine anchor ad's height (see
                // components/ads/anchor-ad-dock-sync.tsx) — that bar paints above
                // this drawer, so its height has to come out of ours or the Sign In
                // row sits underneath it. Defaulted to 0px because the var is
                // REMOVED, not zeroed, when nothing is docked.
                // The safe-area term is NOT redundant with the dock: layout.tsx
                // sets viewport-fit=cover, so 100dvh spans the ~34px behind the
                // home indicator. Without it the Sign In row clears the ad but
                // still lands under the indicator on a notched phone whenever
                // no ad is serving (dock = 0).
                maxHeight: `calc(100dvh - ${HEADER_OFFSET_MOBILE + 16}px - var(--wg-bottom-dock-height, 0px) - env(safe-area-inset-bottom))`,
                // Keeps the touch drag from chaining to the page once the list
                // hits either scroll end.
                overscrollBehavior: "contain",
              }}
            >
              {/* Signed out only — see `authAtTop`. Sign-in was the drawer's
                  13th row and sat below the fold on every phone, which is a
                  poor place for a third of the conversion triad. */}
              {authAtTop && (
                <div className="px-1 pb-1">
                  <LoginButton variant="mobile" />
                </div>
              )}

              {/* Close lives in the pill's top-right X (functional while open).
                  This row is just the drawer's section heading. */}
              <div className="px-4 pb-1">
                <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-green-700">What do you need?</span>
              </div>

              <Link
                href={HOME_UPLOAD_HREF}
                prefetch={false}
                onClick={(e) => handleIdentifyClick(e, "mobile_menu_identify_grass")}
                className="group flex min-h-[48px] items-center gap-3 rounded-2xl px-4 py-2.5 text-base font-semibold text-gray-900 transition-colors hover:bg-white/55"
              >
                <span className="grid h-9 w-9 flex-none place-items-center rounded-xl bg-linear-to-r from-green-600 to-emerald-600 text-white shadow-[0_7px_16px_-7px_rgba(22,163,74,0.6)]">
                  <UiIcon icon={ScanSearch} className="h-5 w-5" />
                </span>
                Identify my grass
              </Link>

              <Link href="/diagnose" prefetch={false} onClick={closeMenu} className="group flex min-h-[48px] items-center gap-3 rounded-2xl px-4 py-2.5 text-base font-semibold text-gray-900 transition-colors hover:bg-white/55">
                <span className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-white/70 bg-white/60 text-green-700"><Stethoscope className="h-5 w-5" /></span>
                Diagnose a lawn problem
              </Link>

              <div>
                <button
                  onClick={toggleTools}
                  className="group flex min-h-[48px] w-full items-center justify-between rounded-2xl px-4 py-2.5 text-base font-semibold text-gray-900 transition-colors duration-300 hover:bg-white/55 hover:text-green-700 touch-manipulation"
                  aria-expanded={isToolsExpanded}
                  aria-controls="mobile-tools-panel"
                >
                  <span className="flex items-center gap-3">
                    <span className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-white/70 bg-white/60 text-green-700 transition-transform duration-200 group-hover:scale-105">
                      <Calculator className="h-5 w-5" />
                    </span>
                    <span className="text-left">Calculate what I need</span>
                  </span>
                  <UiIcon icon={ChevronDown}
                    className={`w-5 h-5 transition-transform duration-200 ${
                      isToolsExpanded ? 'rotate-180' : ''
                    }`} 
                  />
                </button>
                {isToolsExpanded && (
                  <div id="mobile-tools-panel" className="ml-3 mt-2 space-y-1 rounded-2xl border border-white/55 bg-white/35 p-3">
                    {/* Label stays now that the inline search is gone: it marks
                        these six as a subset, which the full-directory link
                        below then answers. */}
                    <div className="px-1 pb-1 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                      Popular tools
                    </div>
                    {POPULAR_TOOLS.map((tool) => (
                        <Link
                          key={tool.href}
                          href={tool.href}
                          prefetch={false}
                          onClick={closeMenu}
                          className="group flex min-h-[52px] items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors duration-150 hover:bg-white/70 touch-manipulation"
                        >
                          <span className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-green-100 bg-green-50/80 text-green-700 transition-transform duration-200 group-hover:scale-105">
                            <ToolIllustration href={tool.href} />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-sm text-gray-900 leading-snug">{tool.name}</div>
                            <div className="mt-0.5 line-clamp-1 text-xs text-gray-600">{tool.category} · {tool.estimatedTime}</div>
                          </div>
                        </Link>
                    ))}
                    <Link
                      href="/tools#tool-directory"
                      prefetch={false}
                      onClick={closeMenu}
                      className="group flex min-h-[44px] items-center justify-between gap-2 rounded-xl px-3 py-2 text-sm font-semibold text-green-700 transition-colors duration-200 hover:bg-white/70 touch-manipulation"
                    >
                      <span className="inline-flex items-center gap-2"><Search className="h-4 w-4" />Search all {ACTIVE_TOOL_COUNT} tools</span>
                      <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                )}
              </div>

              <Link href="/products" prefetch={false} onClick={closeMenu} className="group flex min-h-[48px] items-center gap-3 rounded-2xl px-4 py-2.5 text-base font-semibold text-gray-900 transition-colors hover:bg-white/55">
                <span className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-white/70 bg-white/60 text-green-700"><ShoppingBag className="h-5 w-5" /></span>
                Shop lawn products
              </Link>

              <div className="mx-4 my-1.5 border-t border-white/50" />
              <div className="px-4 pb-1 text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">Browse</div>
              {/* Two-up. Seven full-width rows were 504px — half the drawer —
                  which pushed the tail of this list and the auth control below
                  the fold on every phone. Halving the row COUNT is the only
                  lever big enough to fit an iPhone SE; trimming padding alone
                  is not. */}
              <div className="grid grid-cols-2 gap-1">
                {mobileBrowseLinks.map(renderMobileNavLink)}
              </div>

              {!authAtTop && (
                <div className="px-4 py-3 border-t border-white/40">
                  <LoginButton variant="mobile" />
                </div>
              )}
            </nav>
            </div>
          </div>
          </>
        )}
    </header>
  )
}
